import { lruMemoize } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/browser';

import { ISG_FOLDER_EXTERNAL_ID } from '@iconik-io/core/src/constants/isg';
import type {
  AssetsAssetSchemaTypedWithId,
  AssetsCollectionElasticSchemaWithIdNullable,
} from '@iconik-io/iconik-api/src/services/assets';
import type {
  FilesFormatSchemaWithId,
  FilesFormatSchemaWithIdNullable,
  FilesProxySchemaWithId,
} from '@iconik-io/iconik-api/src/services/files';

import type { SearchItemEntity } from '../redux/entities/normalized/search_item';
import { getObjectTypeFromAssetOrSearchItem } from './get_object_type_from_asset_or_search_item';
import { getProxyMediaType } from './get_proxy_media_type';
import {
  isGenericMediaType,
  MEDIA_TYPE_AUDIO,
  MEDIA_TYPE_COLLECTION,
  MEDIA_TYPE_COLLECTION_ISG,
  MEDIA_TYPE_IMAGE,
  MEDIA_TYPE_VIDEO,
  type MediaType,
  playableMediaTypes,
} from './media_type';

export const isCollectionMappedToStorage = (
  collection: Partial<AssetsCollectionElasticSchemaWithIdNullable> | undefined,
): boolean =>
  Boolean(collection?.storage_id) ||
  collection?.external_id === ISG_FOLDER_EXTERNAL_ID;

export const getFormatMediaType = (
  format: FilesFormatSchemaWithIdNullable | undefined,
): MediaType | undefined => {
  const rawMimeType = format?.metadata?.[0]?.internet_media_type;
  const mimeType = typeof rawMimeType === 'string' ? rawMimeType : undefined;

  if (mimeType) {
    if (mimeType.includes('video') || mimeType.includes('application/mxf')) {
      return MEDIA_TYPE_VIDEO;
    }
    if (mimeType.includes('audio')) {
      return MEDIA_TYPE_AUDIO;
    }
    if (mimeType.includes('image')) {
      return MEDIA_TYPE_IMAGE;
    }
  }
  const components = format?.components;
  if (components?.some(({ type }) => type === 'VIDEO')) {
    return MEDIA_TYPE_VIDEO;
  }
  if (components?.some(({ type }) => type === 'AUDIO')) {
    return MEDIA_TYPE_AUDIO;
  }
  if (components?.every(({ type }) => type === 'IMAGE')) {
    return MEDIA_TYPE_IMAGE;
  }

  return undefined;
};

const searchItemMediaType: Record<
  NonNullable<SearchItemEntity['type']>,
  MediaType
> = {
  ASSET: 'file',
  NLE_PROJECT: 'nle_project',
  CUSTOM: 'custom',
  PLACEHOLDER: 'placeholder',
  LINK: 'link',
  SEQUENCE: 'sequence',
  SUBCLIP: 'subclip',
  POST: 'file',
};

export const getSearchItemMediaType = lruMemoize(
  (
    searchItem: SearchItemEntity | AssetsAssetSchemaTypedWithId | undefined,
    formats: FilesFormatSchemaWithId[],
    proxies: FilesProxySchemaWithId[],
  ): MediaType | undefined => {
    if (!searchItem) {
      return undefined;
    }

    let mediaType: MediaType | undefined;
    switch (getObjectTypeFromAssetOrSearchItem(searchItem)) {
      case 'assets': {
        if (searchItem.type === 'ASSET') {
          const itemMediaType =
            'media_type' in searchItem ? searchItem.media_type : undefined;
          if (itemMediaType && isGenericMediaType(itemMediaType)) {
            mediaType = itemMediaType;
          } else {
            const formatsValue =
              'formats' in searchItem ? searchItem.formats : formats;

            const originalFormat = formatsValue?.find((format) => {
              if (format.name === null) {
                Sentry.captureMessage(
                  `Null returned in formats name in asset with ID: ${searchItem.id}`,
                );
              }
              return format.name?.toUpperCase() === 'ORIGINAL';
            });
            mediaType = getFormatMediaType(originalFormat);
          }
        }
        if (searchItem.type === 'SUBCLIP') {
          return 'subclip';
        }
        if (searchItem.type !== 'LINK') {
          const proxy =
            'proxies' in searchItem ? searchItem.proxies?.[0] : proxies?.[0];

          if (!mediaType && proxy) {
            mediaType = getProxyMediaType(proxy);
          }
        }
        if (!mediaType) {
          mediaType = searchItemMediaType[searchItem.type ?? 'ASSET'];
        }
        break;
      }
      case 'collections': {
        mediaType = isCollectionMappedToStorage(searchItem)
          ? MEDIA_TYPE_COLLECTION_ISG
          : MEDIA_TYPE_COLLECTION;

        break;
      }
      default:
        break;
    }

    return mediaType;
  },
);

/**
 * Whether the player could ever play this item, judged from the media type
 * carried by the search item itself. Answers before formats and proxies are
 * fetched, so treat it as provisional: an item indexed without a usable media
 * type reads as unplayable until the fetched media data says otherwise.
 */
export const isSearchItemMediaTypePlayable = (
  searchItem: SearchItemEntity | AssetsAssetSchemaTypedWithId | undefined,
): boolean =>
  playableMediaTypes.has(getSearchItemMediaType(searchItem, [], []));
